import { useDispatch } from 'react-redux';

import {
  PageSizeOption,
  usePersistedPageSize,
} from '../../../../hooks/usePersistedPageSize';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { takeBase } from '../types';

/**
 * Tarea #11 — Pegamento entre `usePersistedPageSize` y la tabla server-side.
 *
 * Centraliza el patrón duplicado en cada tabla migrada (AccidentsTable,
 * CampaignTable, InputRadioArrowTable, …):
 *
 *   1. Llama a `usePersistedPageSize(tableId)` para obtener el take persistido
 *      POR TABLA. Cada tableId tiene su propia key en localStorage para que el
 *      selector y el cursor de Redux (que también es por tabla) estén alineados.
 *   2. Calcula `takePagination` que reemplaza a `takeBase` en la tabla:
 *      - Si la tabla está activa para el selector (`enabled === true`) usa
 *        el take persistido.
 *      - Si NO (modal) cae a `fallbackTake` (default `takeBase`).
 *   3. Construye `paginationProps` listo para esparcir en `<PaginationButtons>`:
 *      - `pageSize`, `onPageSizeChange`, `pageSizeOptions` solo se rellenan
 *        cuando el selector debe mostrarse (i.e. listing principal, no modal).
 *   4. El `onPageSizeChange` resultante:
 *      - Persiste la nueva preferencia (localStorage + Redux cursor).
 *      - Resetea el server-side page index a 1 (cambiar take siempre debe
 *        volver a la primera página, alineado con el patrón UX general).
 *      - Dispara `onReFetchData` si la tabla lo proporciona.
 *
 * Histórico: la primera versión del PR ocultaba el selector en tablet y
 * forzaba `takeBase`. Tras review se decidió mismo comportamiento en todos
 * los viewports — el scroll en tablet es aceptable y la consistencia
 * desktop/tablet evita sorpresas (cross-tab sync, RBAC móvil, etc.).
 */

export interface UseServerPageSizeParams {
  /** El `tableId` que identifica la tabla en Redux. */
  tableId: string;
  /**
   * Solo se activa el selector si la tabla está siendo usada en su contexto
   * principal de listado server-side (no en un modal, no en un wizard, …).
   * Pasar `tableId === tableIds.MAIN_LISTING_ID` aquí.
   */
  enabled: boolean;
  /**
   * El cursor actual de la tabla (filtros **commiteados** vía botón Lupa /
   * Redux). Se hace `{ ...selectorCursor, ...liveFilters, take, cursor: '' }`
   * al cambiar el page-size para no perder filtros.
   */
  selectorCursor: Record<string, unknown>;
  /**
   * Filtros **vivos del form** (estado local del componente, NO comprometidos
   * a Redux todavía). Si están presentes, sus valores definidos sobrescriben
   * a `selectorCursor` al cambiar take. Mismo patrón que `handleSearch` y
   * `fetchOrderID` usan internamente con `selectorData?.X ?? selectorCursor?.X`.
   *
   * Sin este parámetro, al cambiar el take se ignorarían los filtros que el
   * usuario tiene escritos en el form pero aún no ha aplicado con la lupa,
   * dejando una inconsistencia visual: inputs marcados pero query sin filtros.
   */
  liveFilters?: Record<string, unknown>;
  /** Setter del estado de página server-side (la página dentro de la paginación). */
  setCurrentServerPage: (page: number) => void;
  /** Trigger de refetch tras cambiar el take. */
  onReFetchData?: () => void | Promise<void>;
  /**
   * Take fallback cuando el selector NO está activo. Default: `takeBase`.
   * CampaignTable lo usa para pasar su `pageSizeModal` responsivo cuando la
   * tabla se usa en un modal donde el selector no aplica.
   */
  fallbackTake?: number;
}

export interface ServerPageSizePaginationProps {
  pageSize?: PageSizeOption;
  onPageSizeChange?: (next: PageSizeOption) => void;
  pageSizeOptions?: readonly PageSizeOption[];
}

export interface UseServerPageSizeReturn {
  /** Take efectivo a usar en las queries y cálculos de paginación. */
  takePagination: number;
  /** Spread directo en `<PaginationButtons {...paginationProps} />`. */
  paginationProps: ServerPageSizePaginationProps;
  /** Útil para gates extra en el render. `true` => el selector se mostrará. */
  isPageSizeSelectorActive: boolean;
}

export const useServerPageSize = ({
  tableId,
  enabled,
  selectorCursor,
  liveFilters,
  setCurrentServerPage,
  onReFetchData,
  fallbackTake = takeBase,
}: UseServerPageSizeParams): UseServerPageSizeReturn => {
  const dispatch = useDispatch();
  const {
    pageSize: serverPageSize,
    setPageSize: setServerPageSize,
    availableOptions,
  } = usePersistedPageSize(tableId);

  // Active = la tabla está en su contexto de listado server-side. No hay regla
  // de viewport: el selector se muestra/no se muestra solo según el contexto
  // (listing principal vs modal).
  const active = enabled;

  const takePagination = active ? serverPageSize : fallbackTake;

  const onPageSizeChange = active
    ? (next: PageSizeOption) => {
        setServerPageSize(next);
        setCurrentServerPage(1);
        // Merge: filtros vivos del form (`liveFilters`) sobrescriben los
        // commiteados (`selectorCursor`) cuando están definidos. Mismo patrón
        // que `handleSearch`/`fetchOrderID` aplican con `selectorData ?? selectorCursor`.
        // Sin esto, cambiar el take ignoraría los inputs que el usuario tiene
        // escritos pero aún no ha aplicado con la lupa → inconsistencia visual.
        const mergedCursor: Record<string, unknown> = { ...selectorCursor };
        if (liveFilters) {
          for (const k of Object.keys(liveFilters)) {
            const v = liveFilters[k];
            if (v !== undefined) mergedCursor[k] = v;
          }
        }
        dispatch(
          setCursor(tableId, {
            ...mergedCursor,
            take: next,
            cursor: '',
          }),
        );
        void onReFetchData?.();
      }
    : undefined;

  // Nota sobre primera carga ever (sin redux-persist hidratado, sin click previo):
  // Tras subir `takeBase` a 25 (alineado con `DESKTOP_DEFAULT`), el fallback
  // del loader (`cursor.take ?? takeBase`) coincide con la preferencia que el
  // selector muestra por defecto. Solo persiste un mismatch si el usuario tiene
  // un valor distinto de 25 en localStorage (e.g. cross-tab desde otra sesión):
  // el selector mostrará 50/100 pero el primer fetch usará 25 (takeBase).
  // Es un mismatch cosmético de UNA carga; el siguiente click sincroniza
  // ambos vía `onPageSizeChange` y redux-persist mantiene coherencia desde
  // entonces. Decisión consciente: no añadir mount-sync que dispararía un
  // refetch extra en TODAS las primeras cargas (rompería `multimedia-no-refetch`).

  return {
    takePagination,
    paginationProps: {
      pageSize: active ? (serverPageSize as PageSizeOption) : undefined,
      onPageSizeChange,
      pageSizeOptions: active ? availableOptions : undefined,
    },
    isPageSizeSelectorActive: active,
  };
};
